'use client'
import styled from 'styled-components'
import { CategorySubNav } from '@/features/shop/components/CategorySubNav'
import { screenSizes } from '@shipt/design-system-themes'
import { spacingRem } from '@/features/shared/theme/tokens'
import { RightMenuItems } from '@/features/shared/components/Headers/Header/RightMenuItems'
import { HeaderSearch } from '@/features/shared/components/Headers/Header/HeaderSearch'
import { ShoppingAgentEntryButton } from '@/features/shop/components/ShoppingAgent/ShoppingAgentEntryButton'
import { ShoppingAgentScript } from '@/features/shop/components/ShoppingAgent/ShoppingAgentScript'
import { LogoAndStoreNav } from '@/features/shared/components/Headers/Header/LogoAndStoreNav'
import { HamburgerMenuButton } from '@/features/shared/components/HamburgerMenu/HamburgerMenuButton'
import { HeaderBanners } from '@/features/shared/components/Headers/Header/HeaderBanners'
import { MobileStoreHeader } from '@/features/shared/components/Headers/Header/MobileStoreHeader'
import { STICKY_HEADER_ID, MOBILE_STICKY_HEADER_ID } from '@/features/shared/hooks/useHeaderRect'
import { MainHeaderWrapper } from '@/features/shared/components/Headers/Header/styles'
import { Row } from '@shipt/design-system-layouts'
import { HEADER_Z_INDEX } from '@/features/shared/constants/global'
import { useUserShoppingStore } from '@/features/shop/services/ShoppingStore/hooks'

type Props = {
  renderPlaPopover?: () => React.ReactNode
}

export const Header = ({ renderPlaPopover }: Props) => {
  const { data: shoppingStore } = useUserShoppingStore()

  return (
    <>
      <HeaderBanners />
      <ShoppingAgentScript />
      <NonMobileHeaderContainer id={STICKY_HEADER_ID}>
        <TopHeaderBar>
          <MainHeaderWrapper contentMaxWidth as="nav">
            <Row align="center" justify="center" spacing={{ base: 'lg', lg: 'xl' }}>
              <Row spacing="sm">
                <HamburgerMenuButton />
                <LogoAndStoreNav
                  renderPlaPopover={renderPlaPopover}
                  logo={shoppingStore?.mp_store_logo_image_url}
                  color={shoppingStore?.mp_store_logo_background_color}
                />
              </Row>
              <AutocompleteWrapper hasPlaPopover={Boolean(renderPlaPopover)}>
                <HeaderSearch />
              </AutocompleteWrapper>
              <ShoppingAgentEntryButton />
              <RightMenuItems />
            </Row>
          </MainHeaderWrapper>
        </TopHeaderBar>
        <CategorySubNavContainer>
          <CategorySubNav />
        </CategorySubNavContainer>
      </NonMobileHeaderContainer>
      <MobileStickyContainer id={MOBILE_STICKY_HEADER_ID}>
        <MobileStoreHeader
          shouldAnimate
          logo={shoppingStore?.mp_store_logo_image_url}
          color={shoppingStore?.mp_store_logo_background_color}
        />
      </MobileStickyContainer>
    </>
  )
}

const NonMobileHeaderContainer = styled.header`
  width: 100%;
  z-index: ${HEADER_Z_INDEX};
  top: 0;
  overflow: visible;
  position: sticky;

  @media ${screenSizes.max_mobile} {
    display: none;
  }
`

const AutocompleteWrapper = styled.div<{ hasPlaPopover: boolean }>`
  flex: 1 1 auto;
  display: block;
  z-index: ${({ hasPlaPopover }) => (hasPlaPopover ? HEADER_Z_INDEX : 'auto')};
  min-width: 12.5rem;
`

const MobileStickyContainer = styled.header`
  position: sticky;
  z-index: 999;
  top: 0;
  margin-bottom: ${spacingRem('xxl')};
  pointer-events: none;

  @media ${screenSizes.tablet} {
    display: none;
  }
`

const CategorySubNavContainer = styled.div`
  position: relative;
  z-index: -1;

  @media ${screenSizes.max_mobile} {
    display: none;
  }
`

const TopHeaderBar = styled.div`
  position: relative;
  z-index: ${HEADER_Z_INDEX - 1};
  background-color: ${({ theme }) => theme.color.background.base.neutral.subtle_00};
`
